import { Anchor, Button, Group, Stack, Switch, Text } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { getUrlString, notificationHandler, Yup } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { Policy as PolicyType } from '@/types'

import * as api from './api'
import * as SignupFormFields from './Fields'

interface Props {
  channel?: string
  policies?: PolicyType[]
}

export default function Signup({ channel, policies }: Props) {
  // Hooks
  const router = useRouter()
  const { login } = useAuth() || {}

  // Constants
  const termsOfUse = policies?.find(
    policy => policy.tags?.includes('terms-of-use') && policy.content
  )
  const privacyPolicy = policies?.find(
    policy => policy.tags?.includes('privacy-policy') && policy.content
  )
  const hasAtLeastOnePolicy = !!termsOfUse || !!privacyPolicy
  const hasBothPolicies = !!termsOfUse && !!privacyPolicy

  // Schema validation
  const schema = Yup.object().shape({
    email: Yup.string().email().required(),
    firstName: Yup.string().min(2).max(50).required(),
    lastName: Yup.string().min(2).max(150).required(),
    password: Yup.string().min(8).required(),
    confirmPassword: Yup.string()
      .required()
      .oneOf([Yup.ref('password'), null], 'As senhas não conferem')
  })

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    validate: yupResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
    clearInputErrorOnChange: false
  })

  // Constants
  const { callbackUrl, site, email } = router.query
  const siteSlug = getUrlString(site)
  const callbackUrlSlug = getUrlString(callbackUrl)
  const emailSlug = getUrlString(email)

  // States
  const [loading, setLoading] = useState(false)
  const [terms, setTerms] = useState(false)

  // Actions
  const handleSubmit = async () => {
    setLoading(true)

    try {
      const policiesUid = []
      if (termsOfUse) policiesUid.push(termsOfUse.uid)
      if (privacyPolicy) policiesUid.push(privacyPolicy.uid)

      const { status } = await api.signup({
        site: siteSlug,
        userData: form.values,
        ...(channel ? { channel } : {}),
        ...(policiesUid.length ? { policies: policiesUid } : {})
      })
      if (status === 201) {
        try {
          await login?.({ login: form.values.email, password: form.values.password })
          // TEMP: router.push(callbackUrlSlug || '/accounts/profile')
          router.push(
            `/accounts/complete-profile${
              callbackUrlSlug ? `?callbackUrl=${encodeURIComponent(callbackUrlSlug)}` : ''
            }`
          )
        } catch (error) {
          notificationHandler({
            variant: 'error',
            message: 'Ocorreu um erro ao fazer sua autenticação. Faça o login novamente.'
          })
        }
      } else {
        notificationHandler({
          variant: 'error',
          message: 'Ocorreu um erro ao fazer o cadastro. Tente novamente mais tarde.'
        })
      }
    } catch (error) {
      const { response } = error as AxiosError
      if (response?.status === 400) {
        notificationHandler({
          variant: 'error',
          message: Object.values(response.data || {}).join(' ')
        })
      } else {
        notificationHandler({
          variant: 'error',
          message: 'Ocorreu um erro ao fazer o cadastro. Tente novamente mais tarde.'
        })
      }
    }
    setTimeout(() => {
      setLoading(false)
    }, 1000)
  }

  // Effects
  useEffect(() => {
    if (emailSlug && !form.values.email) form.setFieldValue('email', emailSlug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSlug])

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack align="center">
        <SignupFormFields.FirstName
          inputProps={{ ...form.getInputProps('firstName'), w: '100%' }}
        />
        <SignupFormFields.LastName inputProps={{ ...form.getInputProps('lastName'), w: '100%' }} />
        <SignupFormFields.Email inputProps={{ ...form.getInputProps('email'), w: '100%' }} />
        <SignupFormFields.Password inputProps={{ ...form.getInputProps('password'), w: '100%' }} />
        <SignupFormFields.ConfirmPassword
          inputProps={{ ...form.getInputProps('confirmPassword'), w: '100%' }}
        />

        {hasAtLeastOnePolicy && (
          <>
            <Text size="sm">
              Ao acessar e utilizar esta plataforma, independente do dispositivo, você{' '}
              {!!termsOfUse && (
                <>
                  concorda com os{' '}
                  <Anchor component={Link} href="/terms" target="_blank">
                    Termos de Uso
                  </Anchor>
                </>
              )}
              {hasBothPolicies && ' e '}
              {!!privacyPolicy && (
                <>
                  declara estar ciente e de acordo com a{' '}
                  <Anchor component={Link} href="/privacy" target="_blank">
                    Política de Privacidade
                  </Anchor>
                </>
              )}
              .
            </Text>
            <Switch
              label={`Estou de acordo com${termsOfUse ? ' os termos de uso' : ''}${hasBothPolicies ? ' e' : ''}${privacyPolicy ? ' as políticas de privacidade' : ''} da plataforma`}
              onChange={() => setTerms(!terms)}
            />
          </>
        )}
      </Stack>
      <Group position="apart" mt="xl">
        <Anchor
          component={Link}
          size="sm"
          href={`/accounts/login${callbackUrlSlug ? `?callbackUrl=${callbackUrlSlug}` : ''}`}>
          Já possui conta?
        </Anchor>
        <Button type="submit" loading={loading} disabled={hasAtLeastOnePolicy && !terms}>
          Criar conta
        </Button>
      </Group>
    </form>
  )
}
